import { motion } from "framer-motion";
import { HeartHandshake, Sparkles, Users, Coffee, Activity, ArrowUpRight } from "lucide-react";
import { SERVICES, BUSINESS } from "@/data/site";

const ICONS = { HeartHandshake, Sparkles, Users, Coffee, Activity };

const card = {
  hidden: { opacity: 0, y: 30 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const ImageCard = ({ s, i }) => {
  const Icon = ICONS[s.icon];
  return (
    <motion.div
      custom={i}
      variants={card}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      data-testid={`service-card-${s.key}`}
      className={`group relative overflow-hidden rounded-3xl soft-shadow ${
        s.span === "tall" ? "lg:row-span-2" : ""
      } ${s.span === "wide" ? "lg:col-span-2" : ""}`}
    >
      <img
        src={s.image}
        alt={s.title}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/25 to-transparent" />
      <div className="relative flex flex-col justify-end h-full min-h-[16rem] p-7 text-sand">
        <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-sand/15 backdrop-blur-sm">
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="font-serif text-2xl">{s.title}</h3>
        <p className="mt-2 text-sand/85 text-sm leading-relaxed max-w-md">{s.desc}</p>
      </div>
    </motion.div>
  );
};

const PlainCard = ({ s, i }) => {
  const Icon = ICONS[s.icon];
  return (
    <motion.div
      custom={i}
      variants={card}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      data-testid={`service-card-${s.key}`}
      className="group relative overflow-hidden rounded-3xl border border-charcoal/10 bg-white/60 p-7 backdrop-blur-sm transition-transform duration-300 hover:-translate-y-1"
    >
      <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-full bg-sage/15 text-sage-700">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-serif text-2xl text-charcoal">{s.title}</h3>
        <ArrowUpRight className="h-5 w-5 text-charcoal/30 transition-colors group-hover:text-sage" />
      </div>
      <p className="mt-2 text-charcoal-soft text-sm leading-relaxed">{s.desc}</p>
    </motion.div>
  );
};

export const Services = () => {
  return (
    <section id="services" data-testid="services-section" className="relative py-24 sm:py-32 bg-sand-deep">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl mb-14">
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="eyebrow mb-4">
            How Nicole Helps
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-serif text-4xl sm:text-5xl text-charcoal tracking-tight leading-tight"
          >
            Personal, non-medical care — shaped around your loved one.
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-[minmax(16rem,auto)]">
          {SERVICES.map((s, i) =>
            s.image ? <ImageCard key={s.key} s={s} i={i} /> : <PlainCard key={s.key} s={s} i={i} />
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 rounded-3xl bg-charcoal text-sand p-8 sm:p-10"
        >
          <p className="font-serif text-2xl sm:text-3xl max-w-xl leading-snug">
            Not sure which type of care is the right fit? Nicole is happy to talk it through and help you decide.
          </p>
          <a
            href="#contact"
            data-testid="services-cta-button"
            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-sage px-7 py-4 font-medium hover:bg-sage-dark transition-colors"
          >
            Start the conversation
          </a>
        </motion.div>
      </div>
    </section>
  );
};
